// 학부모 페이지 PWA 설치 안내 배너
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7일

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // 이미 설치된 앱이면 표시 안 함
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    // @ts-ignore - iOS standalone 체크
    if ((window.navigator as any).standalone === true) return;

    // 닫기 후 7일 지났는지 확인
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DURATION) return;

    // iOS 판별
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isiOS) {
      setIsIOS(true);
      setShowBanner(true);
      return;
    }

    // 안드로이드: beforeinstallprompt 이벤트 수신
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  if (!showBanner) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'primary.light',
        backgroundColor: '#f0f9ff',
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
        {isIOS ? <IosShareIcon /> : <GetAppIcon />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
          홈 화면에 추가하세요
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {isIOS
            ? '하단 공유 버튼 → "홈 화면에 추가"를 눌러주세요'
            : '앱처럼 바로 접속할 수 있습니다'}
        </Typography>
      </Box>

      {!isIOS && (
        <Button
          variant="contained"
          size="small"
          onClick={handleInstall}
          sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600 }}
        >
          설치
        </Button>
      )}

      <IconButton size="small" onClick={handleDismiss} sx={{ color: 'text.secondary' }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
